import Grid from "@mui/material/Grid";
import DanceDay from "./DanceDay.jsx";
import  {useState} from "react";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Alert from "@mui/material/Alert";
import StarIcon from '@mui/icons-material/Star';
import Link from "@mui/material/Link";
import {AddEventButton} from "./Button/AddEventButton.jsx";
import {useEvents} from "./DanceEventProvider.jsx";
import { useRSVP } from "./RSVPContext.jsx";
import {useAuth} from "./Auth/AuthPage.jsx";
import Chip from "@mui/material/Chip";
import {today} from "../shared/date.js";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";

const FILTER_STORAGE_KEY = "calendarFilters";
const defaultFilters = {
    danceableOnly: false,
    showLessons: true,
    myEventsOnly: false,
    specialEventsOnly: false,
};

const loadFilters = () => {
    try {
        const stored = JSON.parse(window.localStorage.getItem(FILTER_STORAGE_KEY));
        return { ...defaultFilters, ...(stored || {}) };
    } catch {
        return { ...defaultFilters };
    }
};

const saveFilters = (filters) => {
    try {
        window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
    } catch (error) {
        console.error("Error saving filters:", error);
    }
};

const OriginalCalendar = (props) => {

    const {loggedIn} = useAuth()
    const {isRSVP}  = useRSVP();
    const {events, loading} = useEvents();


    // Load filters from localStorage as a single object
    const [filters, setFilters] = useState(loadFilters());

    const { danceableOnly, showLessons, myEventsOnly, specialEventsOnly } = filters;

    const setFilter = (key, value) => {
        const updated = { ...filters, [key]: value };
        setFilters(updated);
        saveFilters(updated);
    };


    const danceableLabel = <strong><Link underline={"none"}>Dancer Favorites Only</Link></strong>;
    const lessonLabel = <strong>Lessons</strong>;
    const myEventsLabel = <strong><StarIcon style={{color: "yellow", position: "relative", top: "5px"}} /> Only</strong>;
    const specialEventsLabel = <Chip color="error" label="Special Events Only" />;

    const toggleDanceable = (e) => setFilter("danceableOnly", e.target.checked);
    const toggleLessons = (e) => setFilter("showLessons", e.target.checked);
    const toggleMyEventsOnly = (e) => setFilter("myEventsOnly", e.target.checked);
    const toggleSpecialEventsOnly = (e) => setFilter("specialEventsOnly", e.target.checked);

    var filteredEvents = danceableOnly
        ? filterDanceableEvents(events)
        : events;

    if (!showLessons) {
        filteredEvents = filterEventInstructors(filteredEvents, false);
    }

    if (myEventsOnly && loggedIn) {
        filteredEvents = filterRsvpEvents(filteredEvents, isRSVP);
    }

    if (specialEventsOnly) {
        filteredEvents = filterSpecialEvents(filteredEvents);
    }

    const upcomingSpecialEventsCount = Array.isArray(events)
        ? events.filter(event => event.special === true && event.date && event.date >= today()).length
        : 0;

    var groupedEvents = groupEventsByDate(filteredEvents)
    let contents;
    if (loading && events === null) {
        contents = <Alert style={{width: "100%"}}>Loading Events</Alert>
    } else if (myEventsOnly && !loggedIn) {
        contents = <Alert style={{width: "100%"}} variant={"filled"} severity={"error"}>You must be logged in to show starred events. Disable filter or log-in to continue.</Alert>
    } else {
        contents = groupedEvents.map((danceDay) => <Grid item key={danceDay.date} xs={12} style={{paddingBottom: "20px"}}><DanceDay danceDay={danceDay} danceableOnly={danceableOnly}/></Grid>)
    }

    return <Grid container>
            <Grid item xs={12} style={{paddingBottom: "20px"}}>
                <Alert severity={"info"} variant="filled" icon={false}>
                    {`${upcomingSpecialEventsCount} Upcoming special events. Use the "Special Events Only" filter to view them. `}
                </Alert>
            </Grid>
            {props.showFilters && <Grid item key={"danceable"} xs={12} style={{paddingBottom: "20px"}}>
                <Card>
                    <CardContent>
                        <Grid container spacing={0}>
                            <Grid item xs={12} md={3}><FormControlLabel label={danceableLabel} control={<Switch color={"primary"} checked={danceableOnly} onChange={toggleDanceable} />} /></Grid>
                            <Grid item xs={12} md={3}><FormControlLabel label={lessonLabel} control={<Switch color={"primary"} checked={showLessons} onChange={toggleLessons} />} /></Grid>
                            <Grid item xs={12} md={3}><FormControlLabel label={myEventsLabel} control={<Switch color={"primary"} checked={myEventsOnly} onChange={toggleMyEventsOnly} />} /></Grid>
                            <Grid item xs={12} md={3}><FormControlLabel label={specialEventsLabel} control={<Switch color={"primary"} checked={specialEventsOnly} onChange={toggleSpecialEventsOnly} />} /></Grid>
                            <Grid item xs={12}><Divider spacing={2}/></Grid>
                            <Grid item xs={12}><Box display="flex" justifyContent="center"><AddEventButton /></Box></Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>}
            <Grid item xs={12}>
                {contents}
            </Grid>
          </Grid>
}

function groupEventsByDate(events) {
    if (!Array.isArray(events)) return [];
    const grouped = {};

    events.forEach(event => {
        if (!event.date) return;
        if (!grouped[event.date]) {
            grouped[event.date] = [];
        }
        grouped[event.date].push(event);
    });

    // Convert to array of { date, events } and sort by date ascending
    return Object.keys(grouped)
        .sort()
        .map(date => ({
            date,
            events: grouped[date]
        }));
}

/**
 * Filters events to only those that have at least one danceable event_artist.artist.
 * @param {Array} events - Array of event objects.
 * @returns {Array} - Filtered array of events.
 */
function filterDanceableEvents(events) {
    if (!Array.isArray(events)) return [];
    return events
        .map(event => {
            // Filter event_artists to only danceable artists
            const danceableArtists = Array.isArray(event.event_artists)
                ? event.event_artists.filter(ea => ea.artist && ea.artist.danceable)
                : [];
            // Only include event if it has at least one danceable artist
            if (danceableArtists.length === 0) return null;
            return {
                ...event,
                event_artists: danceableArtists
            };
        })
        .filter(event => event !== null); // Remove events that were filtered out
}

/**
 * Filters out all event_instructors if showLessons is false.
 * @param {Array} events - Array of event objects.
 * @param {boolean} showLessons - Whether to include instructors.
 * @returns {Array} - Events with event_instructors filtered as needed.
 */
function filterEventInstructors(events) {
    return events.map(event => ({
        ...event,
        event_instructors: []
    }));
}

/**
 * Filters events to only those that the user has RSVP'd to.
 * @param {Array} events - Array of event objects.
 * @param {Function} isRSVP - Function that takes rsvpKey and returns true if RSVP'd.
 * @returns {Array} - Filtered array of events.
 */
function filterRsvpEvents(events, isRSVP) {
    if (!Array.isArray(events) || typeof isRSVP !== "function") return [];
    return events.filter(event => {
        const rsvpKey = `${event.date}-${event.venue?.venueKey}`;
        return isRSVP(rsvpKey);
    });
}

/**
 * Filters events to only those marked as special.
 * @param {Array} events - Array of event objects.
 * @returns {Array} - Filtered array of events.
 */
function filterSpecialEvents(events) {
    if (!Array.isArray(events)) return [];
    return events.filter(event => event.special === true);
}

export default OriginalCalendar;